import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotify } from '@/components/snackbar';
import { useMemo, useState } from 'react';
import {
  createPlatformConfig,
  deletePlatformConfig,
  sendWebhookTest,
  updatePlatformConfig,
} from '@/services/apps';
import {
  getWebhookApps,
  getWebhookAppsSource,
  getWebhookPlatforms,
  getWebhookStatusMetrics,
  WEBHOOK_DATA_SOURCES,
} from '@/services/webhooks';
import {
  getWebhookAppPlatforms,
  getDefaultWebhookPlatform,
  getWebhookPlatformIds,
  mapWebhook,
  mapWebhookMetrics,
} from '@/utils/webhooks';

export default function useWebhooks() {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const [testingWebhookId, setTestingWebhookId] = useState(null);
  const [deletingWebhookId, setDeletingWebhookId] = useState(null);

  const {
    data: platforms = [],
    isFetching: isFetchingPlatforms,
    refetch: refetchPlatforms,
  } = useQuery({
    queryKey: ['webhook-platforms'],
    queryFn: getWebhookPlatforms,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });

  const webhookPlatform = useMemo(() => getDefaultWebhookPlatform(platforms), [platforms]);
  const webhookAppPlatforms = useMemo(() => getWebhookAppPlatforms(platforms), [platforms]);
  const webhookAppsSource = getWebhookAppsSource();
  const webhookPlatformIds = useMemo(
    () => getWebhookPlatformIds(platforms, webhookPlatform),
    [platforms, webhookPlatform]
  );
  const getCurrentWebhookPlatformIds = () => webhookPlatformIds;

  const {
    data: metricsData = null,
    isFetching: isFetchingMetrics,
    refetch: refetchMetrics,
  } = useQuery({
    queryKey: ['webhook-status-metrics'],
    queryFn: getWebhookStatusMetrics,
    staleTime: 60 * 1000,
  });

  const {
    data: webhookAppsData = { count: 0, results: [] },
    isFetching: isFetchingWebhooks,
    refetch: refetchWebhooks,
  } = useQuery({
    queryKey: [
      'webhook-apps',
      webhookAppsSource,
      webhookAppPlatforms.map((platform) => platform.id).sort().join(','),
    ],
    queryFn: () => getWebhookApps({ platforms: webhookAppPlatforms }),
    enabled:
      webhookAppsSource === WEBHOOK_DATA_SOURCES.AGGREGATED || webhookAppPlatforms.length > 0,
    placeholderData: { count: 0, results: [] },
    staleTime: 60 * 1000,
  });

  const { mutateAsync: triggerWebhookTest } = useMutation({
    mutationFn: ({ appId, events }) => sendWebhookTest({ appId, events }),
  });

  const { mutateAsync: createWebhook } = useMutation({
    mutationFn: async (data) => {
      const { triggerType, ...restData } = data;
      const { webhookV1Id, webhookV2Id } = webhookPlatformIds;
      const targetPlatformId = triggerType === 'grouped' ? webhookV2Id : webhookV1Id;

      if (!targetPlatformId) {
        const missingPlatformType = triggerType === 'grouped' ? 'webhook_v2' : 'webhook';

        throw new Error(
          `Nao foi possivel identificar a plataforma ${missingPlatformType}. Recarregue a pagina e tente novamente.`
        );
      }

      return createPlatformConfig({
        platformId: targetPlatformId,
        ...restData,
      });
    },
    onSuccess: () => {
      notify.success('Webhook criado com sucesso.');
      queryClient.invalidateQueries({ queryKey: ['webhook-apps'] });
      queryClient.invalidateQueries({ queryKey: ['webhook-status-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['webhook-activities'] });
    },
    onError: (error) => {
      notify.error(error.message || 'Nao foi possivel criar o webhook.');
    },
  });

  const { mutateAsync: updateWebhook } = useMutation({
    mutationFn: ({ appId, ...data }) =>
      updatePlatformConfig({
        appId,
        ...data,
      }),
    onSuccess: () => {
      notify.success('Webhook atualizado com sucesso.');
      queryClient.invalidateQueries({ queryKey: ['webhook-apps'] });
      queryClient.invalidateQueries({ queryKey: ['webhook-status-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['webhook-activities'] });
    },
    onError: (error) => {
      notify.error(error.message || 'Nao foi possivel atualizar o webhook.');
    },
  });

  const { mutateAsync: triggerWebhookDelete } = useMutation({
    mutationFn: ({ appId }) => deletePlatformConfig({ appId }),
    onSuccess: () => {
      notify.success('Webhook excluído.');
      queryClient.invalidateQueries({ queryKey: ['webhook-apps'] });
      queryClient.invalidateQueries({ queryKey: ['webhook-status-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['webhook-activities'] });
    },
    onError: (error) => {
      notify.error(error.message || 'Não foi possível excluir o webhook.');
      // A falha pode ser um 404 de webhook já excluído em outra aba: recarregar a lista tira ele
      // da tela sem que a tela precise interpretar o erro.
      queryClient.invalidateQueries({ queryKey: ['webhook-apps'] });
    },
  });

  const metrics = useMemo(() => mapWebhookMetrics(metricsData), [metricsData]);
  const rawWebhooks = useMemo(() => webhookAppsData.results || [], [webhookAppsData.results]);
  const webhooks = useMemo(() => rawWebhooks.map(mapWebhook), [rawWebhooks]);

  const testWebhook = async (webhook) => {
    const eventIds = (webhook?.events || []).map((event) => event.id).filter(Boolean);

    if (!webhook?.id || eventIds.length === 0) {
      notify.warning('Esse webhook precisa ter eventos configurados para testar.');
      return;
    }

    setTestingWebhookId(webhook.id);

    try {
      await triggerWebhookTest({
        appId: webhook.id,
        events: eventIds,
      });

      notify.success('Evento de teste enviado com sucesso.');
    } catch (error) {
      notify.error(error.message || 'Nao foi possivel enviar o evento de teste.');
    } finally {
      setTestingWebhookId(null);
    }
  };

  const deleteWebhook = async ({ appId }) => {
    setDeletingWebhookId(appId);

    try {
      return await triggerWebhookDelete({ appId });
    } finally {
      setDeletingWebhookId(null);
    }
  };

  return {
    metrics,
    rawWebhooks,
    webhooks,
    webhookPlatforms: platforms,
    webhookPlatform,
    getWebhookPlatformIds: getCurrentWebhookPlatformIds,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    deletingWebhookId,
    testingWebhookId,
    testWebhook,
    isFetching: isFetchingPlatforms || isFetchingMetrics || isFetchingWebhooks,
    isFetchingMetrics,
    isFetchingPlatforms,
    isFetchingWebhooks,
    refetchMetrics,
    refetchPlatforms,
    refetchWebhooks,
  };
}
